<template>
    <div v-if="form.WorkOrderId" class="alert alert-info d-flex align-items-center mb-3 py-2">
        <i class="fas fa-lock me-2"></i>
        <span>This item is linked to <a href="javascript:void(0);" @click="$router.push('/invoices/edit/' + form.WorkOrderId)"><strong>Invoice {{ form.WorkOrder?.InvoiceNumber || '#' + form.WorkOrderId }}</strong></a> and cannot be edited.</span>
    </div>
    <fieldset :disabled="!!form.WorkOrderId">
        <div class="row g-3">
            <div class="col-lg-6">
                <div class="mb-2">
                    <i class="fas fa-briefcase me-2 text-muted"></i>
                    <strong>Job details</strong>
                </div>
                <div class="card h-100">
                    <div class="card-body">
                        <div class="mb-3">
                            <label class="d-block">
                                <span class="form-label">Technician <span class="text-danger">*</span></span>
                                <tech-ac 
                                    v-model="form.AssignedTo" 
                                    placeholder="Search for a technician..."
                                ></tech-ac>
                            </label>
                        </div>
                        <div class="mb-3">
                            <label class="d-block">
                                <span class="form-label">Client <span class="text-danger">*</span></span>
                                <client-ac 
                                    v-model="form.CustomerNumber" 
                                    inputclass="form-control form-control-sm" 
                                    placeholder="Search for a client..."
                                ></client-ac>
                            </label>
                        </div>
                        <div class="mb-3">
                            <label class="d-block">
                                <span class="form-label">Request/Job <span class="text-danger">*</span></span>
                                <job-ac 
                                    v-model="form.RequestId" 
                                    :client="form.CustomerNumber"
                                    placeholder="Search for a request..."
                                ></job-ac>
                            </label>
                        </div>
                        <div class="row">
                            <div class="col-md-6">
                                <label class="d-block mb-3">
                                    <span>Work Date <span class="text-danger">*</span></span>
                                    <div class="input-group input-group-sm">
                                        <button 
                                            class="btn btn-outline-secondary" 
                                            type="button"
                                            @click="adjustWorkDate(-1)"
                                            title="Previous day"
                                        >
                                            <i class="fa fa-chevron-left"></i>
                                        </button>
                                        <input type="datetime-local" 
                                            class="form-control form-control-sm" 
                                            v-model="form.WorkCompleteDate" 
                                            placeholder="Work Date"
                                        >
                                        <button 
                                            class="btn btn-outline-secondary" 
                                            type="button"
                                            @click="adjustWorkDate(1)"
                                            title="Next day"
                                        >
                                            <i class="fa fa-chevron-right"></i>
                                        </button>
                                    </div>
                                </label>
                            </div>
                            <div class="col-md-6">
                                <label class="d-block mb-3">
                                    <span>Location</span>
                                    <select 
                                        class="form-select form-select-sm" 
                                        v-model="form.TaskLocation"
                                    >
                                        <option :value="undefined">Select Location...</option>
                                        <option 
                                            v-for="loc in locationOptions" 
                                            :key="loc.value" 
                                            :value="loc.value"
                                        >
                                            {{ loc.label }}
                                        </option>
                                    </select>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-lg-6">
                <div class="mb-2">
                    <i class="fas fa-file-invoice-dollar me-2 text-muted"></i>
                    <strong>Billing info</strong>
                </div>
                <div class="card h-100">
                    <div class="card-body">
                        <div class="row">
                            <div class="col-md-6">
                                <label class="d-block mb-3">
                                    <span>Hours Worked <span class="text-danger">*</span></span>
                                    <input 
                                        class="form-control form-control-sm" 
                                        type="number" 
                                        step="0.25"
                                        v-model="form.ActualTime" 
                                        placeholder="Hours" 
                                    />
                                </label>
                            </div>
                            <div class="col-md-6">
                                <label class="d-block mb-3">
                                    <span>Should Bill ($)</span>
                                    <div class="input-group input-group-sm">
                                        <span class="input-group-text">$</span>
                                        <input 
                                            class="form-control form-control-sm" 
                                            type="number" 
                                            step="0.01"
                                            v-model="form.ShouldBill" 
                                            placeholder="0.00" 
                                        />
                                    </div>
                                </label>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-md-6">
                                <label class="d-block mb-3">
                                    <span>Invoice Amount ($)</span>
                                    <div class="input-group input-group-sm">
                                        <span class="input-group-text">$</span>
                                        <input 
                                            class="form-control form-control-sm" 
                                            type="number" 
                                            step="0.01"
                                            v-model="form.InvoiceAmount" 
                                            placeholder="0.00" 
                                        />
                                    </div>
                                </label>
                            </div>
                            <div class="col-md-6">
                                <label class="d-block mb-3">
                                    <span>Invoice Time (hrs)</span>
                                    <input 
                                        class="form-control form-control-sm" 
                                        type="number" 
                                        step="0.25"
                                        v-model="form.InvoiceTime" 
                                        placeholder="0.00" 
                                    />
                                </label>
                            </div>
                        </div>
                        <div class="mb-3">
                            <label class="d-block">
                                <span>Status</span>
                                <select 
                                    class="form-select form-select-sm" 
                                    v-model="form.Status"
                                >
                                    <option :value="undefined">Select Status...</option>
                                    <option 
                                        v-for="status in statusOptions" 
                                        :key="status.value" 
                                        :value="status.value"
                                    >
                                        {{ status.label }}
                                    </option>
                                </select>
                            </label>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-12 mt-5">
                <div class="mb-2">
                    <i class="fas fa-clipboard-list me-2 text-muted"></i>
                    <strong>Notes</strong>
                </div>
                <div class="card">
                    <div class="card-body">
                        <div class="row">
                            <div class="col-md-6">
                                <label class="d-block mb-3">
                                    <span>Work Description</span>
                                    <textarea class="form-control form-control-sm" placeholder="Describe the work performed" v-model="form.ActualWork"
                                        rows="4"></textarea>
                                </label>
                            </div>
                            <div class="col-md-6">
                                <label class="d-block mb-3">
                                    <span>Invoice Text</span>
                                    <textarea class="form-control form-control-sm" placeholder="Text to appear on invoice..." v-model="form.InvoiceText"
                                        rows="4"></textarea>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </fieldset>
</template>

<script lang="ts" setup>
import { computed, watch, ref } from "vue";
import TechAc from "@/components/Techs/TechAc.vue";
import ClientAc from "@/components/Clients/ClientAc.vue";
import JobAc from "@/components/Jobs/JobAc.vue";
import { storeToRefs } from "pinia";
import { useTaskStatusStore } from "@/stores/TaskStatusStore";
import OptionsService from "@/services/OptionsService";
import ApiService from "@/services/ApiService";
import Api from "@/services/Api";

const taskStatusStore = useTaskStatusStore();
const { list: taskStatusList } = storeToRefs(taskStatusStore);

// Convert store list to options array for select dropdown
const statusOptions = computed(() => 
    Object.values(taskStatusList.value).map(status => ({
        value: status.Id,
        label: status.Status
    }))
);

const locationOptions = [
    { value: 1, label: "ON-SITE" },
    { value: 2, label: "GTA" },
    { value: 3, label: "WEB" },
    { value: 4, label: "PHONE/EMAIL" },
    { value: 5, label: "IN HOUSE" },
    { value: 6, label: "OVERSIGHT" }
];

const model = defineModel<any>();

const form = computed({
    get: () => {
        if (model.value && model.value.Type !== 'work') {
            model.value.Type = 'work';
        }
        return model.value || {};
    },
    set: (value) => {
        if (value) {
            value.Type = 'work';
        }
        model.value = value;
    }
});

const clientBillingRates = ref<Record<string, number>>({});
const techBillingRates = ref<Record<string, number>>({});
const jobDepartment = ref<string | null>(null);

// Adjust work date by days
const adjustWorkDate = (days: number) => {
    if (!form.value.WorkCompleteDate) {
        // If no date set, use today
        const today = new Date();
        form.value.WorkCompleteDate = today.toISOString().slice(0, 16);
    }
    
    const currentDate = new Date(form.value.WorkCompleteDate);
    currentDate.setDate(currentDate.getDate() + days);
    form.value.WorkCompleteDate = currentDate.toISOString().slice(0, 16);
};

const loadClientBillingRates = async (customerNumber: string) => {
    if (!customerNumber) {
        clientBillingRates.value = {};
        return;
    }
    
    try {
        const optionKey = 'billing_rates_contact';
        const savedRates = await OptionsService.get(optionKey, 'Contact', customerNumber);
        
        if (savedRates) {
            clientBillingRates.value = JSON.parse(savedRates);
        } else {
            clientBillingRates.value = {};
        }
    } catch (error) {
        console.error('Error loading client billing rates:', error);
        clientBillingRates.value = {};
    }
};

const loadTechBillingRates = async (techId: string) => {
    if (!techId) {
        techBillingRates.value = {};
        return;
    }
    
    try {
        const optionKey = 'billing_rates_technician';
        const savedRates = await OptionsService.get(optionKey, 'Technician', techId);
        
        if (savedRates) {
            techBillingRates.value = JSON.parse(savedRates);
        } else {
            techBillingRates.value = {};
        }
    } catch (error) {
        console.error('Error loading tech billing rates:', error);
        techBillingRates.value = {};
    }
};

const loadJobDepartment = async (requestId: string) => {
    if (!requestId) {
        jobDepartment.value = null;
        return;
    }
    
    try {
        const job = await Api.loadJob(requestId, true);
        jobDepartment.value = job?.DepartmentCategory || null;
    } catch (error) {
        console.error('Error loading job department:', error);
        jobDepartment.value = null;
    }
};

const calculateBilling = () => {
    if (!form.value.ActualTime || !jobDepartment.value) {
        return;
    }
    
    const hours = Number(form.value.ActualTime) || 0;
    if (hours <= 0) return;
    
    let rate: number | null = null;
    
    // Client rate takes precedence
    if (clientBillingRates.value[jobDepartment.value]) {
        rate = clientBillingRates.value[jobDepartment.value];
    }
    // Fall back to technician rate
    else if (techBillingRates.value[jobDepartment.value]) {
        rate = techBillingRates.value[jobDepartment.value];
    }
    
    if (rate !== null && rate > 0) {
        const total = hours * rate;
        form.value.ShouldBill = total;
        form.value.InvoiceAmount = total;
    }
};

// Watch for changes to recalculate billing
watch(() => form.value.CustomerNumber, async (newVal) => {
    if (newVal) {
        await loadClientBillingRates(newVal.toString());
        calculateBilling();
    }
});

watch(() => form.value.AssignedTo, async (newVal) => {
    if (newVal) {
        await loadTechBillingRates(newVal.toString());
        calculateBilling();
    }
});

watch(() => form.value.RequestId, async (newVal) => {
    if (newVal) {
        await loadJobDepartment(newVal.toString());
        calculateBilling();
    }
});

watch(() => form.value.ActualTime, () => {
    calculateBilling();
});
</script>
